<script lang="ts">
import { defineComponent } from 'vue'
export default defineComponent({
  name: 'MaEditor',
})
</script>
<template>
	<div style="border: 1px solid #ccc; z-index: 100; width: 100%">
		<!-- 工具栏 -->
		<Toolbar v-if="!disabled" :editor="editorRef" :mode="mode" style="border-bottom: 1px solid #ccc" />
		<!-- 编辑器 -->
		<Editor
			:model-value="modelValue"
			:style="style"
			:disabled="disabled"
			:default-config="editorConfig"
			:mode="mode"
			@on-created="handleCreated"
			@on-change="handleChange"
		/>
	</div>
</template>

<script lang="ts" setup name="MaEditor">
import '@wangeditor/editor/dist/css/style.css'
import { onBeforeUnmount, shallowRef } from 'vue'
import { useI18n } from 'vue-i18n'
import constant from '@/utils/constant'
const { t } = useI18n()
import { Editor, Toolbar } from '@wangeditor/editor-for-vue'
import { IDomEditor, IEditorConfig } from '@wangeditor/editor'
import cache from '@/utils/cache'
import request from '@/utils/request'
import { Base64 } from 'js-base64'
import { ElMessage } from 'element-plus'

const props = defineProps({
	modelValue: {
		type: String,
		default: ''
	},
	mode: {
		type: String,
		default: 'default' // 可选值：[default | simple]
	},
	placeholder: {
		type: String,
		default: ''
	},
	style: {
		type: String,
		default: 'height: 200px;'
	},
	disabled: {
		type: Boolean,
		default: false
	}
})

// 编辑器实例，必须用 shallowRef
const editorRef = shallowRef()

type InsertFnType = (url: string, alt: string, href: string) => void

// 自定义图片上传函数
const customUploadImg = async (file: File, insertFn: InsertFnType) => {
	try {
		const formData = new FormData()
		formData.append('file', file)

		const res = await request.post(constant.uploadUrl + '?access_token=' + cache.getToken(), formData, {
			headers: {
				'Content-Type': 'multipart/form-data'
			}
		})

		if (res && res.data) {
			insertFn(constant.apiUrl + res.data.url, res.data.name, '')
		}
	} catch (error: any) {
		console.error('图片上传失败：', error)
	}
}

// 自定义图片处理函数 - 统一使用base64
const handleImageAsBase64 = (file: File, insertFn: InsertFnType) => {
	// 检查文件大小，避免base64过大
	const maxSize = 5 * 1024 * 1024 // 2MB
	if (file.size > maxSize) {
		customUploadImg(file, insertFn)
		return
	}

	const reader = new FileReader()
	reader.onload = e => {
		const base64 = e.target?.result as string
		insertFn(base64, file.name, '')
	}
	reader.onerror = () => {
		customUploadImg(file, insertFn)
	}
	reader.readAsDataURL(file)
}

// 编辑器配置
const editorConfig: Partial<IEditorConfig> = {
	placeholder: props.placeholder,
	readOnly: props.disabled,
	MENU_CONF: {
		uploadImage: {
			// 所有图片都优先使用 base64，大文件自动切换到服务器上传
			customUpload: customUploadImg,
			customPaste: customUploadImg
		}
	}
}

// 组件销毁时，也及时销毁编辑器
onBeforeUnmount(() => {
	const editor = editorRef.value
	if (editor == null) {
		return
	}
	editor.destroy()
})

const handleCreated = (editor: IDomEditor) => {
	editorRef.value = editor
}

// 编辑器change事件触发
const emit = defineEmits(['update:modelValue'])
const handleChange = (editor: IDomEditor) => {
	emit('update:modelValue', editor.getHtml())
}
</script>
